import React, { useState, useEffect } from 'react';
import { ArrowLeft, Home, MessageSquare, Users, Bot, Sparkles } from 'lucide-react';
import type { CurrentPage } from '../App';
import { chatNotificationService } from '../services/chatNotificationService';
import { getUserRole } from '../services/teamService';
import type { UserRole } from '../types/team';

interface ChatHubPageProps {
  onNavigate: (page: CurrentPage) => void;
}

const ChatHubPage: React.FC<ChatHubPageProps> = ({ onNavigate }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isLoadingRole, setIsLoadingRole] = useState(true);

  // Load user role
  useEffect(() => {
    const loadUserRole = async () => {
      setIsLoadingRole(true);
      try {
        const role = await getUserRole();
        setUserRole(role);
      } finally {
        setIsLoadingRole(false);
      }
    };
    loadUserRole();
  }, []);

  // Load unread count
  useEffect(() => {
    chatNotificationService.initialize();
    setUnreadCount(chatNotificationService.getUnreadCount());

    // Listen for new messages
    const handleNewMessage = () => {
      setUnreadCount(chatNotificationService.getUnreadCount());
    };

    chatNotificationService.addListener(handleNewMessage);

    return () => {
      chatNotificationService.removeListener(handleNewMessage);
    };
  }, []);

  // Scroll to top when page loads (backup - App.tsx also handles this)
  React.useLayoutEffect(() => {
    // Scroll the actual scrolling container (#root)
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.scrollTop = 0;
    }
    
    // Also try window (for compatibility)
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    console.log('🔝 [ChatHubPage] Scroll reset in useLayoutEffect, #root scrollTop:', rootElement?.scrollTop);
  }, []);

  // Filter chat options based on role - team_admin only gets Team Chat
  const allSubcategories: Array<{ name: string; icon: typeof Bot; targetPage: 'chat' | 'teamChat'; description: string }> = [
    { name: 'Noor AI', icon: Bot, targetPage: 'chat', description: 'AI assistant for property assessments, report generation, and data analysis' },
    { name: 'Team Chat', icon: Users, targetPage: 'teamChat', description: 'Collaborate with your team members on projects and assessments' },
  ];
  
  const subcategories = userRole === 'team_admin' 
    ? allSubcategories.filter(cat => cat.targetPage === 'teamChat')
    : allSubcategories;

  const handleSubcategoryClick = (targetPage: 'chat' | 'teamChat') => {
    console.log(`Navigating to subcategory: ${targetPage}`);
    onNavigate({ page: targetPage });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/30 to-sky-50 pb-20">
      {/* Modern Header with Gradient */}
      <header className="bg-gradient-to-r from-cyan-600 via-cyan-500 to-sky-600 shadow-2xl sticky top-0 z-40 flex-shrink-0">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/0 via-white/10 to-sky-600/0"></div>
        <div className="relative w-full px-4 sm:px-6 flex items-center justify-between app-header-padding">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">
                Chat Hub
              </h1>
              <p className="text-cyan-100 text-sm hidden sm:block">Communication and collaboration tools</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => onNavigate({ page: 'start' })}
              title="Go to Home"
              className="p-2.5 rounded-xl text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200 backdrop-blur-sm border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl"
            >
              <Home size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto p-4 sm:p-6 lg:p-8">
        {/* Welcome Section */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl mt-8 border border-slate-200/50 overflow-hidden">
          <div className="relative bg-gradient-to-r from-slate-50 to-cyan-50/30 px-6 py-5 border-b border-slate-200/50">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
            <div className="relative">
              <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-slate-800 to-cyan-700 bg-clip-text text-transparent mb-2">
                Communication Tools
              </h2>
              <p className="text-slate-600 text-sm">Choose your preferred communication method</p>
            </div>
          </div>
          
          <div className="p-8">
            {isLoadingRole ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-cyan-600 border-t-transparent mb-4"></div>
                  <p className="text-slate-600">Loading chat options...</p>
                </div>
              </div>
            ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
              {subcategories.map((category, index) => (
                <button
                  key={category.name}
                  onClick={() => handleSubcategoryClick(category.targetPage)}
                  className="group relative bg-gradient-to-br from-white via-slate-50/50 to-cyan-50/30 hover:from-cyan-50/50 hover:via-white hover:to-sky-50/40 transition-all duration-300 border-2 border-slate-200/60 hover:border-cyan-300/60 rounded-2xl p-8 flex flex-col items-center justify-center text-center shadow-lg hover:shadow-2xl hover:shadow-cyan-500/10 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:ring-offset-2 transform hover:scale-[1.02] hover:-translate-y-1"
                >
                  {/* Decorative background gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  
                  <div className="relative z-10">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-sky-500/20 rounded-2xl blur-xl transform scale-110 opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
                      <div className="relative bg-gradient-to-br from-cyan-500 to-sky-600 p-4 rounded-2xl shadow-lg shadow-cyan-500/25 group-hover:shadow-xl group-hover:shadow-cyan-500/30 transition-all duration-300">
                        <category.icon className="w-10 h-10 text-white" strokeWidth={2} />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <h3 className="text-lg font-bold text-slate-800 group-hover:text-cyan-700 transition-colors duration-200">
                        {category.name}
                      </h3>
                      {category.targetPage === 'teamChat' && unreadCount > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 bg-gradient-to-r from-red-500 to-pink-600 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-slate-600 leading-relaxed group-hover:text-slate-700 transition-colors duration-200">
                      {category.description}
                    </p>
                    
                    {/* Progress indicator or badge */}
                    <div className="mt-4 inline-flex items-center space-x-1.5 px-3 py-1.5 bg-slate-100 group-hover:bg-cyan-100 rounded-full transition-colors duration-200">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-slate-700 group-hover:text-cyan-700">
                        {category.name === 'Team Chat' ? 'Ready' : 'Ready'}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            )}
          </div>
        </div>

      </main>
    </div>
  );
};

export default ChatHubPage;
